import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  vatReturnClassificationSchema,
  vatReturnReviewInputSchema,
  type VatReturnSourceRow,
  type VatReturnReviewInput,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { FinancialFormProtection } from './financial-form-protection';
import { usePreviewVatReview, useSaveVatReview } from '../api/use-vat-return';
import { formatLedgerMoney } from '../lib/ledger-format';

const amountFields = ['rate', 'net', 'legalTax', 'deductibleTax'] as const;
const normalize = (value: string) =>
  value
    .trim()
    .replace(',', '.')
    .replace(/^(-?)\.(\d+)$/u, '$10.$2')
    .replace(/^(-?\d+)\.$/u, '$1');
const resolver = zodResolver(vatReturnReviewInputSchema);
const emptyBucket = (): VatReturnReviewInput['buckets'][number] => ({
  flow: 'SALE',
  classification: 'DOMESTIC',
  rate: '',
  net: '',
  legalTax: '',
  deductibleTax: '0',
});

export function VatReturnReviewDialog({
  source,
  onClose,
}: {
  source: VatReturnSourceRow;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const previewMutation = usePreviewVatReview(),
    saveMutation = useSaveVatReview();
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof previewMutation.mutateAsync>
  > | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<VatReturnReviewInput>({
    resolver: (values, context, options) =>
      resolver(
        {
          ...values,
          buckets: values.buckets.map((bucket) => ({
            ...bucket,
            rate: normalize(bucket.rate),
            net: normalize(bucket.net),
            legalTax: normalize(bucket.legalTax),
            deductibleTax: normalize(bucket.deductibleTax),
          })),
        },
        context,
        options,
      ),
    defaultValues: {
      journalId: source.journalId,
      reason: '',
      buckets: source.event?.classification
        ? [
            {
              flow: source.event.flow,
              classification: source.event.classification,
              rate: source.event.rate,
              net: source.event.net,
              legalTax: source.event.legalTax,
              deductibleTax: source.event.deductibleTax,
            },
          ]
        : [emptyBucket()],
    },
    shouldFocusError: true,
  });
  const buckets = useFieldArray({ control: form.control, name: 'buckets' });
  useEffect(() => {
    const subscription = form.watch(() => setPreview(null));
    return () => subscription.unsubscribe();
  }, [form]);
  const pending = previewMutation.isPending || saveMutation.isPending;
  const submit = form.handleSubmit(
    async (input) => {
      setError('');
      try {
        if (!preview) {
          setPreview(await previewMutation.mutateAsync(input));
          return;
        }
        await saveMutation.mutateAsync({
          ...input,
          sourceHash: preview.sourceHash,
          version: preview.version,
          idempotencyKey: key,
        });
        setConfirmed(true);
        form.reset(input);
        toast.success(t('vatReturn.saved'));
        onClose();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'vatReturn.errors.request');
      }
    },
    () => {
      setPreview(null);
      setError('vatReturn.errors.source');
    },
  );
  return (
    <FinancialFormProtection
      dirty={form.formState.isDirty && !confirmed}
      pending={pending}
      onClose={onClose}
    >
      {(close) => (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <DialogContent
            closeLabel={t('common.close')}
            className="max-w-3xl"
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              close();
            }}
          >
            <DialogHeader>
              <DialogTitle>
                {t('vatReturn.review')} · {source.number}
              </DialogTitle>
              <DialogDescription>{t('vatReturn.reviewHint')}</DialogDescription>
            </DialogHeader>
            <form
              ref={formRef}
              noValidate
              onSubmit={(event) => void submit(event)}
              className="space-y-4 [&_input]:min-h-11 [&_select]:min-h-11 [&_button]:min-h-11"
              onKeyDown={(event) => {
                if (
                  (event.metaKey || event.ctrlKey) &&
                  (event.key.toLowerCase() === 's' || event.key === 'Enter')
                ) {
                  event.preventDefault();
                  if (!pending) formRef.current?.requestSubmit();
                }
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="vat-review-reason">{t('vatReturn.reason')}</Label>
                <Textarea
                  id="vat-review-reason"
                  {...form.register('reason')}
                  aria-invalid={!!form.formState.errors.reason}
                />
                {form.formState.errors.reason ? (
                  <p className="text-sm text-destructive">{t('vatReturn.errors.source')}</p>
                ) : null}
              </div>
              {buckets.fields.map((bucket, index) => (
                <fieldset key={bucket.id} className="grid gap-3 rounded border p-3 sm:grid-cols-2">
                  <legend className="px-1 text-sm font-medium">
                    {t('vatReturn.bucket')} {index + 1}
                  </legend>
                  <div className="space-y-1">
                    <Label htmlFor={`vat-flow-${bucket.id}`}>{t('vatReturn.flow')}</Label>
                    <select
                      id={`vat-flow-${bucket.id}`}
                      {...form.register(`buckets.${index}.flow`)}
                      className="w-full rounded border bg-background px-2"
                    >
                      <option value="SALE">{t('vatReturn.SALE')}</option>
                      <option value="PURCHASE">{t('vatReturn.PURCHASE')}</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`vat-case-${bucket.id}`}>{t('vatReturn.classification')}</Label>
                    <select
                      id={`vat-case-${bucket.id}`}
                      {...form.register(`buckets.${index}.classification`)}
                      className="w-full rounded border bg-background px-2"
                      aria-invalid={!!form.formState.errors.buckets?.[index]?.classification}
                    >
                      {vatReturnClassificationSchema.options.map((classification) => (
                        <option key={classification} value={classification}>
                          {t(`vatReturn.classes.${classification}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {amountFields.map((field) => (
                    <div key={field} className="space-y-1">
                      <Label htmlFor={`vat-${field}-${bucket.id}`}>{t(`vatReturn.${field}`)}</Label>
                      <Input
                        id={`vat-${field}-${bucket.id}`}
                        inputMode="decimal"
                        {...form.register(`buckets.${index}.${field}`)}
                        aria-invalid={!!form.formState.errors.buckets?.[index]?.[field]}
                      />
                      {form.formState.errors.buckets?.[index]?.[field] ? (
                        <p className="text-sm text-destructive">{t('vatReturn.errors.source')}</p>
                      ) : null}
                    </div>
                  ))}
                  {buckets.fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={pending}
                      onClick={() => buckets.remove(index)}
                    >
                      {t('common.delete')}
                    </Button>
                  ) : null}
                </fieldset>
              ))}
              <Button
                type="button"
                variant="outline"
                disabled={pending || buckets.fields.length >= 500}
                onClick={() => buckets.append(emptyBucket())}
              >
                {t('vatReturn.addBucket')}
              </Button>
              {preview ? (
                <div className="space-y-1 rounded border p-3" aria-live="polite">
                  <p>
                    {t('vatReturn.output')}:{' '}
                    {formatLedgerMoney(preview.ledgerOutputTax, 'EUR', i18n.language)}
                  </p>
                  <p>
                    {t('vatReturn.input')}:{' '}
                    {formatLedgerMoney(preview.ledgerInputTax, 'EUR', i18n.language)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {preview.date} · {t('ledgerClosing.version')} {preview.version}
                  </p>
                </div>
              ) : null}
              {error ? (
                <div
                  role="alert"
                  className="rounded border border-destructive p-3 text-sm text-destructive"
                >
                  {t(error)}
                </div>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="outline" disabled={pending} onClick={close}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={pending}>
                  {t(preview ? 'vatReturn.apply' : 'vatReturn.preview')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </FinancialFormProtection>
  );
}
